import { useEffect, useState } from 'react'

import ProcessingError from 'features/ProcessingError/ProcessingError'
import ProcessingFile from 'features/ProcessingFile/ProcessingFile'
import UploadedFile from 'features/UploadedFile/UploadedFile'
import { type IMediaItem } from 'types/interfaces/ITypes'

interface PostVideoProps {
  isEdit: boolean
  editVideo?: IMediaItem
  id: number
  processing: boolean
  percentage: number
  uploadError: string | null
  previewUrl: string
  locked: boolean
  isPremium: boolean
  abortUploadCb: () => void
  removeFile: () => void
  removeByUrl?: (_url: string) => void
  reorderActive?: boolean
  reorderNumber?: number
  updateOrder?: () => void
  editPreview: () => void
  imagePreview?: string | null
  trimmedVideo?: { start: number; end: number }
  previewOn?: boolean
  handleToggleMediaLocked: (_idx: number) => void
  handleTogglePreviewOn: (_idx: number) => void
  setPreviewOpen?: (open: boolean) => void
  setPreviewMedia?: (postId: string) => void
  mediaDta?: any

  /** NEW: true only when all 10 client thumbs are generated */
  thumbsReady?: boolean
  attachmentCount?: number
}

export const PostVideo = ({
  isEdit,
  id,
  processing,
  percentage,
  uploadError,
  previewUrl,
  locked,
  isPremium,
  abortUploadCb,
  removeFile,
  removeByUrl,
  reorderActive,
  reorderNumber,
  updateOrder,
  editPreview,
  imagePreview,
  trimmedVideo,
  previewOn,
  handleToggleMediaLocked,
  handleTogglePreviewOn,
  setPreviewOpen,
  setPreviewMedia,
  mediaDta,
  thumbsReady = false, // default false so we *only* show poster when explicitly ready
  attachmentCount
}: PostVideoProps) => {

const [posterReady, setPosterReady] = useState(false)

useEffect(() => {
  if (!imagePreview || !thumbsReady) {
    setPosterReady(false)
    return
  }

  const img = new Image()
  img.src = imagePreview

  img.decode()
    .then(() => setPosterReady(true))
    .catch(() => setPosterReady(true))
}, [imagePreview, thumbsReady])


  if (uploadError) {
    return <ProcessingError uploadError={uploadError} onClose={removeFile} />
  }

const isUploading = processing && percentage < 100
const waitingForReady = !processing && !thumbsReady

if (processing || waitingForReady) {
  return (
    <ProcessingFile
      percentage={Math.min(Number(percentage ?? 0), 100)}
      text={isUploading ? 'Uploading…' : 'Processing…'}
      onAbort={() => {
        if (isUploading) abortUploadCb?.()
        removeFile()
      }}
      className='processingFile__video'
    />
  )
}

  // Playable only when server is done *and* thumbs are ready
const videoReady =
  !processing &&
  !uploadError &&
  thumbsReady &&
  !!previewUrl

  if (videoReady) {
    return (
      <UploadedFile
        type='video'
        fileBlob={previewUrl}
        removeFile={isEdit ? () => removeByUrl?.(previewUrl) : removeFile}
        toggleEdit={() => {
          if (!reorderActive) {
            // edit handled via editPreview
          } else {
            updateOrder?.()
          }
        }}
        disablePlay={reorderActive}
        customClass={`${reorderActive ? 'newpost__reorder__file' : ''}`}
        setPreviewOpen={setPreviewOpen}
        mediaDta={mediaDta}
        setPreviewMedia={setPreviewMedia}
        hasReorderCircle={reorderActive}
        reorderNumber={reorderNumber}
        editPreview={editPreview}
        attachmentCount={attachmentCount}
        imagePreview={imagePreview ?? undefined} // poster: first client thumb
        trimmedVideo={trimmedVideo}
        cover={id === 0}
        isPremium={isPremium}
        locked={locked}
        handleToggleMediaLocked={() => handleToggleMediaLocked(id)}
        /* handleTogglePreviewOn={() => handleTogglePreviewOn(id)} */
        /* previewOn={previewOn} */
      />
    )
  }

  // Fallback (shouldn’t hit, but safe)
  return null
}
